// 22章 舗装工事 — やさしい解説
// 原文は src/spec_index.json（PDFから自動生成）。このファイルは手書きで管理する。
// SVGは currentColor を使い、ライト/ダーク両テーマに追従させる。

import type { Commentary } from "../types";

const SVG_PAVEMENT_LAYERS = `
<svg viewBox="0 0 660 250" role="img" aria-label="舗装の断面構成。下から路床（地盤・締固め度90％以上）、その上に路盤（砕石など・締固め度93％以上）、いちばん上に表層のアスファルトまたはコンクリート（締固め度94％以上）を重ねる。路盤と表層の間にはプライムコート（乳剤）を散布して密着させる。荷重は上から下の各層へ分散して伝わる">
  <g fill="currentColor" opacity="0.12"><rect x="80" y="175" width="500" height="55"/></g>
  <g fill="currentColor" opacity="0.28"><rect x="80" y="120" width="500" height="55"/></g>
  <g fill="currentColor" opacity="0.5"><rect x="80" y="88" width="500" height="32"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><rect x="80" y="88" width="500" height="142"/><line x1="80" y1="120" x2="580" y2="120"/><line x1="80" y1="175" x2="580" y2="175"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1" stroke-dasharray="3 3"><line x1="80" y1="120" x2="580" y2="120"/></g>
  <g fill="currentColor" font-size="13" font-weight="bold">
    <text x="95" y="108">表層（アスファルト／コンクリート）</text>
    <text x="95" y="152">路盤（砕石など）</text>
    <text x="95" y="207">路床（地盤）</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.85" text-anchor="end">
    <text x="565" y="108">締固め度94％以上</text>
    <text x="565" y="152">締固め度93％以上</text>
    <text x="565" y="207">締固め度90％以上</text>
  </g>
  <g fill="currentColor" font-size="11.5" opacity="0.8"><text x="360" y="118">▲ プライムコート（乳剤）で密着</text></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><path d="M330 40 v34 M330 74 l-6 -8 M330 74 l6 -8"/></g>
  <g fill="currentColor" font-size="12" opacity="0.8"><text x="342" y="52">荷重は上から各層へ分散</text><text x="80" y="30" font-weight="bold">舗装の断面構成（22章）</text></g>
</svg>`;

const SVG_ASPHALT_WORK = `
<svg viewBox="0 0 660 220" role="img" aria-label="アスファルト舗装の施工手順。①仕上げた路盤に直ちにプライムコート（乳剤）を1.5リットル毎平方メートル程度散布する。②アスファルトフィニッシャで加熱混合物を110℃以上で敷き均す。③ローラーで転圧して締め固め、勾配を確保して水たまりのないよう仕上げる。気温5℃以下は原則施工しない">
  <g fill="none" stroke="currentColor" stroke-width="1.5">
    <rect x="30" y="70" width="180" height="110" rx="8"/>
    <rect x="240" y="70" width="180" height="110" rx="8"/>
    <rect x="450" y="70" width="180" height="110" rx="8"/>
  </g>
  <g fill="currentColor" font-size="14" font-weight="bold" text-anchor="middle">
    <text x="120" y="98">①プライムコート</text>
    <text x="330" y="98">②敷均し</text>
    <text x="540" y="98">③転圧・仕上げ</text>
  </g>
  <g fill="currentColor" opacity="0.5"><rect x="55" y="150" width="130" height="14"/></g>
  <g fill="currentColor" opacity="0.3"><circle cx="80" cy="125" r="2"/><circle cx="110" cy="122" r="2"/><circle cx="140" cy="128" r="2"/><circle cx="165" cy="124" r="2"/></g>
  <g fill="currentColor" opacity="0.7"><rect x="265" y="150" width="130" height="16"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><rect x="300" y="120" width="60" height="24" rx="3"/></g>
  <g fill="currentColor" opacity="0.85"><rect x="475" y="150" width="130" height="14"/><circle cx="520" cy="128" r="16"/></g>
  <g fill="currentColor" font-size="11.5" opacity="0.85">
    <text x="120" y="135" text-anchor="middle">乳剤1.5L/㎡程度</text>
    <text x="330" y="115" text-anchor="middle">フィニッシャ・110℃以上</text>
    <text x="540" y="105" text-anchor="middle">ローラー</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8"><text x="30" y="205">気温5℃以下は原則施工しない／雨天時は直ちに中止（22.4.5）</text></g>
</svg>`;

const SVG_CONCRETE_JOINT = `
<svg viewBox="0 0 660 220" role="img" aria-label="コンクリート舗装の目地。版が膨張収縮で割れるのを防ぐため、縦方向に突合せ目地、横方向に一定間隔で収縮目地を設ける。桝の周囲や建物との取合いには伸縮調整目地を設ける。目地の溝には注入目地材を充填し、版の伸縮に順応させる">
  <g fill="none" stroke="currentColor" stroke-width="1.5"><rect x="60" y="60" width="540" height="120"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5"><line x1="240" y1="60" x2="240" y2="180"/><line x1="420" y1="60" x2="420" y2="180"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.5" stroke-dasharray="6 4"><line x1="150" y1="60" x2="150" y2="180"/><line x1="330" y1="60" x2="330" y2="180"/><line x1="510" y1="60" x2="510" y2="180"/></g>
  <g fill="currentColor" opacity="0.6"><rect x="150" y="60" width="4" height="120"/><rect x="330" y="60" width="4" height="120"/><rect x="510" y="60" width="4" height="120"/></g>
  <g fill="currentColor" font-size="12">
    <text x="245" y="120" opacity="0.85">突合せ目地（縦）</text>
    <text x="60" y="205" opacity="0.85">収縮目地（横・一定間隔）：破線</text>
    <text x="360" y="205" opacity="0.85">桝周囲・建物取合いは伸縮調整目地</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8"><text x="60" y="45" font-weight="bold">コンクリート舗装の目地（22.5.4・表22.5.3）</text></g>
</svg>`;

const SVG_INTERLOCK = `
<svg viewBox="0 0 660 230" role="img" aria-label="インターロッキングブロック舗装。砂などのクッション材を所定厚さに仕上げ、その上にブロックを割付け図に従って敷き並べる。車路はヘリンボンボンド（矢羽根状の組み方）とし、かみ合いで荷重に強くする。一次転圧のあと目地に砂を詰め、二次転圧で目地砂を密実に充填する">
  <g fill="currentColor" opacity="0.15"><rect x="60" y="150" width="540" height="26"/></g>
  <g fill="none" stroke="currentColor" stroke-width="1.4">
    <path d="M70 90 h40 v20 h-20 v20 h-40 v-20 h20 z"/>
    <path d="M110 90 h40 v20 h-20 v20 h-40 v-20 h20 z" transform="translate(0,0)"/>
  </g>
  <g fill="none" stroke="currentColor" stroke-width="1.3">
    <path d="M170 95 l25 -14 l14 24 l-25 14 z"/>
    <path d="M209 105 l25 -14 l14 24 l-25 14 z"/>
    <path d="M195 81 l25 -14 l14 24 l-25 14 z"/>
    <path d="M234 91 l25 -14 l14 24 l-25 14 z"/>
    <path d="M184 119 l25 -14 l14 24 l-25 14 z"/>
    <path d="M223 129 l25 -14 l14 24 l-25 14 z"/>
  </g>
  <g fill="currentColor" font-size="12">
    <text x="60" y="80" opacity="0.85">敷き並べ</text>
    <text x="300" y="100" font-weight="bold">ヘリンボンボンド（車路）</text>
    <text x="300" y="122" opacity="0.85">矢羽根状に組み、かみ合いで荷重に強い</text>
    <text x="60" y="168" opacity="0.85">クッション材（砂）30mm程度</text>
  </g>
  <g fill="currentColor" font-size="12" opacity="0.8">
    <text x="60" y="45" font-weight="bold">インターロッキングブロック舗装（22.8）</text>
    <text x="60" y="205">一次転圧→目地砂詰め→二次転圧で目地砂を密実に充填。段差は3mm以内</text>
  </g>
</svg>`;

const ch22: Record<string, Commentary> = {
  "22.1.1": {
    plainSummary:
      "この章は、主として構内の舗装工事に適用する。1章とセットで適用する。",
    why: "舗装は、地盤（路床）の上に砕石の層（路盤）を敷き、その上をアスファルトやコンクリート等で仕上げる「層構造」です。上からの荷重を各層で分散して地盤に伝えます。この章は路床→路盤→各種舗装（アスファルト・コンクリート・カラー・透水性・ブロック系）→砂利敷き、の順で構成されます。",
    diagrams: [
      {
        svg: SVG_PAVEMENT_LAYERS,
        caption: "舗装の断面構成（22章）",
      },
    ],
  },

  "22.1.2": {
    plainSummary:
      "ゴールは3つ。①所定の材料 ②所定の形状・寸法で仕上り面が所要の状態 ③各層が所定どおり締め固められ耐荷重性を持つこと。",
    why: "舗装の命は「締固め」です。各層をしっかり締め固めないと、沈下・わだち・ひび割れが生じます。層ごとに締固め度（現場密度が基準の何％か）の合格ラインが決められています。",
  },

  "22.1.3": {
    plainSummary:
      "各節に再生材の規定がある場合は再生材を使用する（やむを得ない場合は監督職員と協議）。",
    why: "舗装は再生砕石・再生アスファルトなどリサイクル材の利用が進んだ分野です。環境配慮のため、原則として再生材を使うことが求められます。",
  },

  "22.2.1": {
    plainSummary: "この節は、舗装の路床（いちばん下の地盤の層）に適用する。",
    glossary: [
      {
        term: "路床（ろしょう）",
        meaning:
          "舗装の下、地盤の表面から約1mの層。舗装全体を支える土台。この上に路盤・表層をのせる。",
      },
    ],
  },

  "22.2.2": {
    plainSummary:
      "路床は路床土＋凍上抑制層／フィルター層で構成（適用・厚さは特記）。仕上り面と設計高さの許容差は+20mm・-30mm以内。締固め度は現場密度が最大乾燥密度の90％以上。",
    why: "路床は舗装全体の土台なので、締固めが甘いと上の舗装ごと沈下します。締固め度90％以上を確保するのが基準。寒冷地では凍上抑制層で、凍結による盛り上がり（凍上）を防ぎます。",
    points: [
      "締固め度は最大乾燥密度の90％以上",
      "寒冷地は凍上抑制層で凍上を防ぐ",
    ],
    glossary: [
      {
        term: "凍上（とうじょう）",
        meaning:
          "土中の水分が凍って体積が増え、地面が盛り上がる現象。舗装をひび割れさせる。抑制層で防ぐ。",
      },
    ],
  },

  "22.2.3": {
    plainSummary:
      "材料。盛土材は特記（表3.2.1の種別）。凍上抑制層材料は21章に準ずる。透水性舗装のフィルター層は75μmふるい通過6％以下の砂。路床安定処理用添加材料はセメント・石灰類（表22.2.1、種類は特記）。",
    glossary: [
      {
        term: "路床安定処理",
        meaning:
          "軟弱な路床にセメントや石灰を混ぜて固め、支持力（CBR）を高める処理。",
      },
    ],
  },

  "22.2.4": {
    plainSummary:
      "施工。不適当な部分・障害物は路床面から300mm程度まで除去し周囲と同材で埋め戻して締固め。切土路床は乱さず掘削、盛土路床は1層200mm程度ごとに適切な含水状態で締固め。埋設管がある部分は経路を確認し損傷しないよう締め固める。",
    why: "盛土を1層200mmずつ締め固めるのは、厚く盛ると内部まで締まらず後で沈下するため。散水して「締固めに適した含水状態」にするのは、乾きすぎても湿りすぎても土がよく締まらないためです。",
    points: [
      "盛土は1層200mm程度ごとに締固め",
      "散水で締固めに適した含水状態にする",
      "埋設管の経路を確認し損傷させない",
    ],
  },

  "22.2.5": {
    plainSummary:
      "試験。路床土のCBR試験（JIS A 1211）、締固め度試験（砂置換法JIS A 1214、埋戻し・盛土部は原則実施）、現場CBR試験（JIS A 1222）は適用が特記。仕上り面・設計高さの測定は500㎡ごと及び端数に1か所。",
    glossary: [
      {
        term: "CBR（支持力比）",
        meaning:
          "路床・路盤の支持力を表す指標。値が大きいほど硬くて丈夫。舗装の厚さ設計の基礎になる。",
      },
    ],
  },

  "22.3.1": {
    plainSummary: "この節は、路床の上に設ける路盤に適用する。",
    glossary: [
      {
        term: "路盤（ろばん）",
        meaning:
          "路床と表層の間の砕石等の層。上からの荷重を分散して路床に伝える。舗装の中核をなす層。",
      },
    ],
  },

  "22.3.2": {
    plainSummary:
      "路盤の厚さは特記。締固め度は現場密度が最大乾燥密度の93％以上。仕上り面の測定値平均と設計高さの許容差は-8mm以内。厚さは設計厚さを下回らないこと。",
    why: "路盤は路床（90％）より高い締固め度93％以上が求められます。上に行くほど荷重が集中するので、しっかり締める必要があるためです。厚さを設計値以上に保つのは、薄いと支持力不足になるからです。",
    points: ["締固め度は最大乾燥密度の93％以上", "厚さは設計厚さを下回らない"],
  },

  "22.3.3": {
    plainSummary:
      "路盤材料はクラッシャラン・粒度調整砕石・再生砕石・鉄鋼スラグ等（表22.3.1、修正CBRや塑性指数の規格、種別は特記）。有害な粘土塊・有機物・ごみを含まず、最適含水比に調整する。",
    glossary: [
      {
        term: "クラッシャラン",
        meaning:
          "岩石を砕いたままの砕石（ふるい分けしていない）。大小の粒が混じり、締め固めるとよくかみ合う。路盤の基本材料。",
      },
    ],
  },

  "22.3.4": {
    plainSummary:
      "施工。路盤材料は1層の締固め後仕上り厚さが200mmを超えないように敷き均し、適切な含水状態で締め固める。締固め機械で転圧し平たんに仕上げる。",
    why: "路盤も路床と同じく1層200mm以下で締めます。厚く敷くと下まで締まらないためです。最適含水比で締めると、砕石粒が最もよくかみ合い最大密度になります。",
    points: ["1層の仕上り厚さ200mm以下で締固め", "最適含水比で締めて最大密度に"],
  },

  "22.3.5": {
    plainSummary:
      "試験。最大乾燥密度はJIS A 1210で確認し承諾を受ける。締固め後、厚さは500㎡ごと及び端数に1か所測定。締固め度試験（砂置換法）の測定箇所は1,000㎡以下で3か所、超える分は1,000㎡ごと及び端数に1か所増す。",
  },

  "22.4.1": {
    plainSummary:
      "この節はアスファルト舗装に適用する（路盤は3節による）。",
    glossary: [
      {
        term: "アスファルト舗装",
        meaning:
          "砕石とアスファルト（石油由来の黒い結合材）を加熱混合したもので仕上げる舗装。施工が速く、車道の定番。",
      },
    ],
  },

  "22.4.2": {
    plainSummary:
      "構成・厚さは特記。締固め度は現場密度が基準密度の94％以上。表層厚さは設計厚さを下回らない。平たん性は特記（なければ通行の支障となる水たまりを生じない程度）。",
    why: "アスファルト舗装は表層に荷重が直接かかるため、締固め度94％以上と各層で最も高い基準です。水たまりは舗装の劣化とスリップの原因なので、勾配を付けて水を流します。",
    points: ["表層の締固め度は基準密度の94％以上"],
  },

  "22.4.3": {
    plainSummary:
      "材料。アスファルトはストレートアスファルト（JIS K 2207）・再生アスファルト（表22.4.1、種類は特記）。プライムコート用乳剤はPK-3。骨材は砕石（JIS A 5001）・再生骨材。フィラーは石粉・消石灰・セメント・回収ダスト・フライアッシュ等（石粉の水分1％以下）。乳剤は製造後60日超は使用しない。",
    glossary: [
      {
        term: "フィラー",
        meaning:
          "アスファルト混合物に加える石粉などの微粉末。すき間を埋めてアスファルトと一体化し、混合物を安定させる。",
      },
      {
        term: "プライムコート",
        meaning:
          "路盤の上に散布する乳剤。路盤と表層のアスファルトを密着させ、路盤を安定させる。",
      },
    ],
  },

  "22.4.4": {
    plainSummary:
      "配合その他（表22.4.4のフィラー規定を含む）。表層の加熱アスファルト混合物は表22.4.5により種類は特記。原則として製造所で製造し、マーシャル安定度試験でアスファルト量を求め配合を定める。試験練り・試験施工後に現場配合を決定し表22.4.6の基準値（安定度4.90kN以上・空隙率3〜6％等）を満たす。混合温度は185℃未満。運搬はダンプをシートで覆い保温。剥離対策として消石灰・セメントや剥離防止剤を用いる。",
    why: "アスファルト混合物は「マーシャル安定度試験」で最適なアスファルト量を決めます。アスファルトが多すぎると流動（わだち）、少なすぎるとひび割れ・骨材の剥離が起きます。運搬時にシートで保温するのは、温度が下がると締固めできなくなるためです。",
    points: [
      "マーシャル安定度試験で配合（アスファルト量）を決定",
      "混合温度は185℃未満、運搬はシート保温",
      "剥離対策に消石灰・セメント等を用いる",
    ],
    glossary: [
      {
        term: "マーシャル安定度試験",
        meaning:
          "アスファルト混合物の供試体を横から圧して、強さ（安定度）と変形（フロー値）を測る試験。最適なアスファルト量を決める。",
      },
      {
        term: "剥離（はくり）",
        meaning:
          "水の作用で骨材とアスファルトがはがれる劣化。消石灰やセメントを加えて防ぐ。",
      },
    ],
  },

  "22.4.5": {
    plainSummary:
      "施工。気温5℃以下は原則施工せず、降雨時は直ちに中止。プライムコート用乳剤は路盤仕上げに続いて直ちに1.5L/㎡程度を均一散布。敷均しはアスファルトフィニッシャで、敷均し時の温度は110℃以上、下層が湿っていないことを確認。所定勾配を確保し水たまりを生じないよう締め固めて仕上げる。継目・構造物接触部は乳剤PK-4を塗布後に締固め密着させる。",
    why: "アスファルトは「熱いうちに」施工するのが鉄則。敷均し温度110℃以上を守るのは、冷えると締固めできず所定の密度が出ないためです。プライムコートを路盤仕上げ直後に散布するのは、路盤が乾いて締まりが緩む前に密着させるためです。",
    points: [
      "敷均し温度は110℃以上（熱いうちに締固め）",
      "プライムコートは1.5L/㎡程度を均一散布",
      "気温5℃以下は原則施工しない",
    ],
    diagrams: [
      {
        svg: SVG_ASPHALT_WORK,
        caption: "アスファルト舗装の施工手順（22.4.5）",
      },
    ],
  },

  "22.4.6": {
    plainSummary:
      "試験。締固め度・表層厚さは切取り（コア採取）試験（2,000㎡以下3個、超える分は2,000㎡ごと及び端数に1個増）。基準密度は最初の混合物から作った3個のマーシャル供試体の密度平均。平たん性は散水のうえ目視。抽出試験は特記（結果と現場配合の差は表22.4.7）。",
    glossary: [
      {
        term: "切取り試験（コア採取）",
        meaning:
          "仕上げた舗装を円柱状に切り抜いて、厚さと締固め度を確認する試験。",
      },
    ],
  },

  "22.5.1": {
    plainSummary:
      "この節はコンクリート舗装に適用する（路盤は3節による）。",
    glossary: [
      {
        term: "コンクリート舗装",
        meaning:
          "コンクリート版で仕上げる舗装。耐久性が高く重荷重に強いが、目地や養生の管理が重要。",
      },
    ],
  },

  "22.5.2": {
    plainSummary:
      "構成・厚さは特記（なければ歩行者用通路のコンクリート版厚70mm）。版厚は設計厚を下回らない。溶接金網は版厚150mmなら表面から1/2程度、200mmなら1/3程度の位置に設ける。平たん性は22.4.2(4)による。",
    why: "溶接金網を版の中に入れるのは、コンクリートのひび割れが広がるのを抑えるため。表面寄りに配置するのは、車輪の荷重で表面に生じる引張力に金網で抵抗させるためです。",
  },

  "22.5.3": {
    plainSummary:
      "材料。コンクリートは無筋（特記なければ普通コンクリート、表22.5.1で車路Fc24・歩行者用通路Fc18等）。目地材は加熱施工式注入目地材（表22.5.2、特記なければ低弾性タイプ）、伸縮調整目地用目地板、溶接金網（鉄線径6mm・網目150mm）。",
  },

  "22.5.4": {
    plainSummary:
      "施工。降雨降雪予想時や打込み中コンクリート温度2℃を下回るおそれがある場合は適切な養生（できなければ打込まない）。路盤仕上げに続いて直ちにプライムコート。型枠は堅固に据付け。コンクリートは表面振動機で振動打ち、フロートで平たん仕上げ後ブラシで粗面仕上げ。溶接金網は2層打ちで下層敷均し後に敷き込む。目地の種類・間隔は特記（なければ表22.5.3、注入目地材深さは車路40mm・歩行者用通路30mm）。",
    why: "コンクリート舗装で最重要なのが「目地」です。コンクリートは乾燥収縮や温度で伸縮し、放っておくと勝手にひび割れます。あらかじめ収縮目地を入れて、ひび割れをそこに集中させて制御します。表面をブラシで粗面仕上げにするのは、雨天時のスリップを防ぐためです。",
    points: [
      "目地でひび割れを制御（収縮・突合せ・伸縮調整目地）",
      "表面はブラシで粗面仕上げ（スリップ防止）",
      "溶接金網はひび割れの拡大を抑える",
    ],
    diagrams: [
      {
        svg: SVG_CONCRETE_JOINT,
        caption: "コンクリート舗装の目地（22.5.4）",
      },
    ],
  },

  "22.5.5": {
    plainSummary:
      "養生。コンクリート温度が2℃を下回るおそれがある場合は初期凍害を防ぐ養生（2℃以上に保つ）。打込み後は表面仕上げ後に初期養生（枠を設けシートで硬化まで覆う、必要に応じ被膜養生）、続いてマット等を敷き散水して湿潤を保ち所定強度まで養生。",
    why: "打ちたてのコンクリートは、水分が奪われると表面がひび割れ（乾燥収縮ひび割れ）、凍ると初期凍害で強度が出ません。散水して湿潤を保ち、低温から守るのが養生の目的です。この22.5.5は他章（20・21章）からも参照される重要な養生規定です。",
    points: ["硬化まで湿潤養生（散水）", "低温時はコンクリート温度2℃以上に保つ"],
  },

  "22.5.6": {
    plainSummary:
      "試験。版厚は型枠据付後に水糸またはレベルで測定（500㎡ごと及び端数に1か所）。平たん性の確認は22.4.6(2)による。",
  },

  "22.6.1": {
    plainSummary:
      "この節はカラー舗装に適用する（路盤は3節による）。",
    glossary: [
      {
        term: "カラー舗装",
        meaning:
          "顔料や着色骨材で色を付けた舗装。バス専用レーン・自転車道・歩道の識別や景観のために使う。",
      },
    ],
  },

  "22.6.2": {
    plainSummary:
      "カラー舗装は加熱系または常温系（種類は特記）。加熱系は表層の結合材がアスファルト混合物または石油樹脂系混合物、締固め度・厚さ・平たん性はアスファルト舗装に準ずる。常温系は工法が特記、着色部の下部はアスファルトまたはコンクリート舗装、着色部の厚さは表22.6.1（ニート工法3〜5mm・塗布工法1mm程度）。",
  },

  "22.6.3": {
    plainSummary:
      "材料。加熱系はアスファルト・骨材・フィラーは22.4.3、顔料は無機系、着色骨材・自然石は特記。ニート工法は結合材にエポキシ樹脂、滑り止め用硬質骨材はエメリー・着色磁器質骨材（表22.6.2）。塗布工法はアクリル系カラー塗布材。",
    glossary: [
      {
        term: "ニート工法",
        meaning:
          "エポキシ樹脂を塗り、その上に硬質の着色骨材をまいて固める工法。滑り止めと着色を兼ねる。",
      },
    ],
  },

  "22.6.4": {
    plainSummary:
      "配合その他。加熱系は22.4.4による（結合材がアスファルトの場合、顔料添加量は質量比5〜7％程度で同量のフィラーを減ずる）。加熱系は試験練りで色見本を作り色合を確認。ニート・塗布工法の配合は特記、施工前に見本で色合確認。",
  },

  "22.6.5": {
    plainSummary:
      "施工。加熱系は22.4.5により色むらなく均一に仕上げる（石油樹脂は温度で変色するため温度管理）。ニート工法は基盤を清掃・乾燥後、エポキシ樹脂を車路1.6kg/㎡以上・歩行者用通路1.4kg/㎡以上散布し、硬質骨材を均一散布（気温5℃以下は保温・加温対策）。塗布工法は基盤清掃・乾燥後、基盤面温度40℃以下で施工。",
  },

  "22.6.6": {
    plainSummary:
      "試験。加熱系は締固め度・舗装厚さ（切取り試験）・平たん性・抽出試験を22.4.6により実施。ニート工法・塗布工法は材料使用量を空袋で管理し、平たん性は22.4.6(2)による。",
  },

  "22.7.1": {
    plainSummary:
      "この節は歩行者用通路の透水性アスファルト舗装に適用する（路盤は3節による）。",
    glossary: [
      {
        term: "透水性舗装",
        meaning:
          "すき間の多い（開粒度）混合物で作り、雨水を舗装体に浸透させる舗装。水たまりを防ぎ雨水を地中へ還す。",
      },
    ],
  },

  "22.7.2": {
    plainSummary:
      "構成は特記。表層厚さは22.4.2(3)。平たん性は特記（なければ著しい不陸がないもの）。",
  },

  "22.7.3": {
    plainSummary:
      "材料。ストレートアスファルト・砕石・フィラーの品質は22.4.3による。",
  },

  "22.7.4": {
    plainSummary:
      "配合その他。開粒度アスファルト混合物の配合は表22.7.1・22.7.2を満たし、マーシャル安定度試験でアスファルト量を求める（歩行者用通路の安定度3.0kN以上・空隙率12％以上・透水係数1×10⁻²cm/s以上）。試験練り・試験施工後に現場配合を決定。混合温度・運搬は22.4.4による。",
    why: "透水性舗装は「空隙率12％以上」とわざとすき間を多くして水を通します。通常のアスファルト（空隙率3〜6％）と逆で、密実にしすぎると水が浸透しません。空隙が多い分、安定度の基準は歩行者用の3.0kN以上と緩めです。",
    points: ["空隙率12％以上・透水係数1×10⁻²cm/s以上で水を通す"],
  },

  "22.7.5": {
    plainSummary:
      "施工は22.4.5による。ただしプライムコートは施工しない。",
    why: "プライムコート（乳剤）を省くのは、路盤との間に膜を作ると水が下へ浸透できなくなるためです。透水性を確保するための重要な違いです。",
  },

  "22.7.6": {
    plainSummary:
      "試験。表層厚さは切取り試験、平たん性は目視、開粒度混合物の抽出試験は22.4.6(3)による。",
  },

  "22.8.1": {
    plainSummary:
      "この節はコンクリート平板舗装・インターロッキングブロック舗装・舗石舗装に適用する（路盤は3節による）。",
    glossary: [
      {
        term: "インターロッキングブロック",
        meaning:
          "かみ合う形のコンクリートブロック。すき間の砂の摩擦とかみ合いで荷重を分散する。歩道・車路・広場に使う。",
      },
    ],
  },

  "22.8.2": {
    plainSummary:
      "構成・厚さは表22.8.1。平板舗装の目地材は砂またはモルタル、舗石の基層はアスファルト（50mm）またはコンクリート版（70mm）、クッション材は砂または空練りモルタル（30mm）。仕上り面は歩行に支障となる段差がなく、平板間・ブロック間・舗石間の段差は3mm以内。",
    points: ["ブロック間・平板間の段差は3mm以内"],
  },

  "22.8.3": {
    plainSummary:
      "材料。コンクリート平板はJIS A 5371（特記なければ厚60mm）。インターロッキングブロックはJIS A 5371（特記なければ車路は曲げ強度5.0N/mm²厚80mm、歩行者用通路は3.0N/mm²厚60mm）。舗石は欠陥のないそろった石材。クッション材の砂・空練りモルタル（1：3）、目地材の砂・モルタル（1：2）の品質が定められる。",
  },

  "22.8.4": {
    plainSummary:
      "施工。クッション材を所定厚さに仕上げ、割付け図に基づきブロック等を敷設して転圧。インターロッキングブロックの車路割付けはヘリンボンボンド。敷設後、一次転圧で平たん性を確保し目地詰め、二次転圧で目地砂をブロック表面まで密実に充填（余分な砂は除去）。平板舗装の目地は砂（3〜5mm）またはモルタル（5〜10mm）を充填。",
    why: "インターロッキングブロックは「目地砂」がかみ合いの要。目地に砂を密実に詰めることで、ブロック同士が噛み合い（インターロック）、荷重を隣のブロックへ分散します。車路をヘリンボンボンド（矢羽根状）にするのは、車輪のずれる力に対して最もかみ合いが強くブロックがずれにくいためです。",
    points: [
      "車路はヘリンボンボンドで敷設（かみ合いが強い）",
      "一次転圧→目地砂詰め→二次転圧で目地砂を密実充填",
    ],
    diagrams: [
      {
        svg: SVG_INTERLOCK,
        caption: "インターロッキングブロック舗装（22.8）",
      },
    ],
  },

  "22.8.5": {
    plainSummary: "舗装の平たん性は目視により確認する。",
  },

  "22.9.1": {
    plainSummary: "この節は構内の砂利敷きに適用する。",
  },

  "22.9.2": {
    plainSummary:
      "砂利敷きは表22.9.1（種別は特記、なければ通路はA種・建築物周囲その他はB種）。A種は下敷き（砕石40mm以下）＋上敷き（砂利・砕石25mm以下）、B種は砂利・砕石45mm以下。",
  },

  "22.9.3": {
    plainSummary:
      "施工。下地は水はけよく勾配をとり地均し・転圧・締固め。A種は下敷きを60mm程度に敷き粘質土・砕石ダスト等を100㎡当たり2㎥敷き均して転圧、上敷きを30mm程度に敷き均す。B種は砂利・砕石を60mm程度に敷き均して仕上げる。",
    why: "砂利敷きも下地の水はけ（勾配）が大切です。水がたまると砂利が沈み、ぬかるみになります。A種で下敷きに粘質土やダストを混ぜるのは、砂利のすき間を埋めて締まりやすく、歩きやすくするためです。",
  },
};

export default ch22;
